import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../utils/api";

export type CaseOkQueueStatus =
  | "pending"
  | "sending"
  | "sent"
  | "skipped"
  | "failed"
  | "cancelled";

export interface CaseOkQueueItem {
  id: number;
  case_id: string;
  allocation_id: number | null;
  service_type: string;
  qc1_by: string;
  status: CaseOkQueueStatus;
  queued_at: string;
  send_at: string;
  sent_at: string | null;
  attempts: number;
  error_message: string | null;
  lab_token: string;
  group_id: string | null;
  group_size: number;
  case_owner: string;
  case_status: string;
  remaining_ms: number;
}

interface CaseOkQueueState {
  items: CaseOkQueueItem[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  loading: boolean;
  actingId: number | null;
  actingGroupId: number | null;
  error: string | null;
}

const initialState: CaseOkQueueState = {
  items: [],
  totalCount: 0,
  totalPages: 0,
  currentPage: 1,
  loading: false,
  actingId: null,
  actingGroupId: null,
  error: null,
};

export const fetchCaseOkQueue = createAsyncThunk(
  "caseOkQueue/fetch",
  async (
    {
      page = 1,
      limit = 50,
      status = "pending",
      search = "",
    }: {
      page?: number;
      limit?: number;
      status?: string;
      search?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await api.get("/auth/admin/caseOkQueue", {
        params: { page, limit, status, search },
      });
      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch Case OK queue",
      );
    }
  },
);

export const updateCaseOkQueueItem = createAsyncThunk(
  "caseOkQueue/update",
  async (
    {
      id,
      action,
      actedBy,
    }: { id: number; action: "cancel"; actedBy: string },
    { rejectWithValue },
  ) => {
    try {
      const res = await api.post(`/auth/admin/caseOkQueue/${id}/${action}`, {
        actedBy,
      });
      return res.data.data as CaseOkQueueItem;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || `Failed to ${action} queue item`,
      );
    }
  },
);

export const cancelCaseOkQueueGroup = createAsyncThunk(
  "caseOkQueue/cancelGroup",
  async (
    { id, actedBy }: { id: number; actedBy: string },
    { rejectWithValue },
  ) => {
    try {
      const res = await api.post(`/auth/admin/caseOkQueue/${id}/cancelGroup`, {
        actedBy,
      });
      return (res.data.data?.ids || []) as number[];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to cancel grouped emails",
      );
    }
  },
);

const caseOkQueueSlice = createSlice({
  name: "caseOkQueue",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCaseOkQueue.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCaseOkQueue.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data || [];
        state.totalCount = action.payload.totalCount || 0;
        state.totalPages = action.payload.totalPages || 0;
        state.currentPage = action.payload.currentPage || 1;
      })
      .addCase(fetchCaseOkQueue.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to fetch queue";
      })
      .addCase(updateCaseOkQueueItem.pending, (state, action) => {
        state.actingId = action.meta.arg.id;
      })
      .addCase(updateCaseOkQueueItem.fulfilled, (state, action) => {
        state.actingId = null;
        state.items = state.items.filter((item) => item.id !== action.payload.id);
        state.totalCount = Math.max(0, state.totalCount - 1);
      })
      .addCase(updateCaseOkQueueItem.rejected, (state, action) => {
        state.actingId = null;
        state.error = (action.payload as string) || "Failed to update queue";
      })
      .addCase(cancelCaseOkQueueGroup.pending, (state, action) => {
        state.actingGroupId = action.meta.arg.id;
      })
      .addCase(cancelCaseOkQueueGroup.fulfilled, (state, action) => {
        state.actingGroupId = null;
        const removed = new Set(action.payload);
        const before = state.items.length;
        state.items = state.items.filter((item) => !removed.has(item.id));
        state.totalCount = Math.max(
          0,
          state.totalCount - (before - state.items.length),
        );
      })
      .addCase(cancelCaseOkQueueGroup.rejected, (state, action) => {
        state.actingGroupId = null;
        state.error = (action.payload as string) || "Failed to cancel group";
      });
  },
});

export default caseOkQueueSlice.reducer;
